import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TvIcon from '@mui/icons-material/Tv';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import QRCode from 'qrcode';
import { yomiriP2P, type P2PState } from '../../cast/YomiriP2PBridge.ts';

export function YomiriTvLinkModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [pin, setPin] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [showCode, setShowCode] = useState(false);
  const [state, setState] = useState<P2PState>('disconnected');
  const [error, setError] = useState('');
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (open) {
      let closeTimer: ReturnType<typeof setTimeout> | undefined;
      const generatedPin = yomiriP2P.startHost();
      setPin(generatedPin);
      setState(yomiriP2P.getState());
      setError(yomiriP2P.getError());

      // Generate direct QR code holding the link code or direct URL
      const linkPayload = `${location.origin}/#cast=${generatedPin}`;
      QRCode.toDataURL(linkPayload, {
        width: 240,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      })
        .then((url) => setQrDataUrl(url))
        .catch(() => {});

      const unsubscribe = yomiriP2P.onState((s) => {
        setState(s);
        setError(yomiriP2P.getError());
        if (s === 'connected') {
          closeTimer = setTimeout(() => closeRef.current(), 2500);
        }
      });

      return () => {
        unsubscribe();
        clearTimeout(closeTimer);
      };
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'var(--y-containerLow, #141117)',
            border: '1px solid var(--y-outlineVariant, rgba(255, 255, 255, 0.08))',
            borderRadius: '20px',
            p: 2.5,
            boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
            textAlign: 'center',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={onClose} size="small" sx={{ color: 'var(--y-onSurfaceVariant)' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0, pb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: 'rgba(255, 55, 95, 0.12)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--y-primary, #ff375f)',
            mb: 1.5,
          }}
        >
          <TvIcon sx={{ fontSize: 28 }} />
        </Box>

        <Typography sx={{ fontSize: 19, fontWeight: 800, color: 'var(--y-onSurface, #f4edf2)', mb: 0.5 }}>
          Vincular Control Móvil
        </Typography>

        <Typography sx={{ fontSize: 13, color: 'var(--y-onSurfaceVariant, #ab9ea7)', maxWidth: 320, mb: 2 }}>
          Escaneá el código QR desde Yomiri en tu teléfono para conectar al instante:
        </Typography>

        {/* High-contrast QR Code Container */}
        {qrDataUrl && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <img
              src={qrDataUrl}
              alt="Código QR de vinculación"
              style={{ width: 190, height: 190, display: 'block' }}
            />
          </Box>
        )}

        {/* Compact Code Badge for Quick Typing */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 12, color: 'var(--y-onSurfaceVariant, #ab9ea7)', mb: 0.75 }}>
            O ingresá este código en el celular:
          </Typography>
          <Box
            sx={{
              display: 'inline-block',
              px: 2,
              py: 0.75,
              bgcolor: 'var(--y-containerHigh, #25202c)',
              border: '1.5px solid var(--y-primary, #ff375f)',
              borderRadius: '10px',
              fontSize: 18,
              fontWeight: 900,
              color: 'var(--y-primary, #ff375f)',
              letterSpacing: '1px',
              overflowWrap: 'anywhere',
              maxWidth: '100%',
              boxSizing: 'border-box',
              fontFamily: 'ui-monospace, monospace',
              userSelect: 'all',
            }}
          >
            {pin}
          </Box>
        </Box>

        {error && (
          <Typography role="alert" sx={{ fontSize: 12, color: 'var(--y-error)', mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Status Indicator */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderRadius: '999px',
            bgcolor: state === 'connected' ? 'rgba(74, 222, 128, 0.12)' : 'var(--y-container, #1b1720)',
            border: '1px solid',
            borderColor:
              state === 'connected'
                ? 'var(--y-success, #4ade80)'
                : 'var(--y-outlineVariant, rgba(255, 255, 255, 0.08))',
          }}
        >
          {state === 'connected' ? (
            <>
              <CheckCircle sx={{ color: 'var(--y-success, #4ade80)', fontSize: 17 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--y-success, #4ade80)' }}>
                ¡Celular conectado con éxito!
              </Typography>
            </>
          ) : (
            <>
              <PhoneAndroidIcon sx={{ color: 'var(--y-onSurfaceVariant)', fontSize: 17 }} />
              <Typography sx={{ fontSize: 12, color: 'var(--y-onSurfaceVariant)' }}>
                Esperando escaneo o PIN...
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
